//React
import React from "react";
import PropTypes from "prop-types";
import Loader from "../misc/loader";
import Button from "../inputs/button";
import { formatTimestamp } from "./formatters";
import { CARD_STATUSES, statusCopy } from "./panelStatus";
//CSS
import "./charts.scss";

/**
 * The chrome every panel dataset shares (ASB-4377, design §7.3): title, a
 * one-line description, an "Updated" stamp, and the loading / empty /
 * missing / forbidden / failed states. The renderer inside (TrendChart,
 * BreakdownBar, PanelTable, KpiStrip) is the caller's; this owns the frame
 * and the states, so a chart and a table fail the same way.
 *
 * A 404 is "not available", a 403 "not available for your account", and any
 * other error is a failure with the server's words when it gave any — the
 * Console's rule, kept so an outage never reads as an empty day.
 *
 * `lead` sits at the top of the body whatever the state: an attached strip
 * (PanelView's AttachedStrip) has its own read and its own states, so the
 * card's failure or empty day is said below it, not instead of it.
 */
function PanelCard({
	id,
	title,
	description,
	computedAt,
	status,
	error,
	message,
	onRetry,
	footer,
	lead,
	children,
	className,
}) {
	const state = status ?? "ready";
	const stamp = formatTimestamp(computedAt);
	let body;
	if (state === "loading") {
		body = (
			<div className="as-panel-card__state as-panel-card__state--loading">
				<Loader size="md" />
			</div>
		);
	} else if (state === "ready") {
		body = children;
	} else {
		const copy = statusCopy(state, { title, error, message });
		body = (
			<div className={`as-panel-card__state as-panel-card__state--${state}`}>
				<p className="as-panel-card__state-title">{copy.headline}</p>
				{copy.detail ? <p>{copy.detail}</p> : null}
				{state === "failed" && onRetry ? (
					<Button
						text="Try again"
						type="button"
						style="light"
						size="sm"
						disabled={false}
						loaded={false}
						onClick={onRetry}
					/>
				) : null}
			</div>
		);
	}
	return (
		<section
			className={["as-panel-card", className ?? ""].join(" ").trim()}
			data-status={state}
			data-dataset={id}
			aria-labelledby={id ? `as-panel-card-${id}` : undefined}
		>
			<header className="as-panel-card__header">
				<div className="as-panel-card__heading">
					<h2
						className="as-panel-card__title"
						id={id ? `as-panel-card-${id}` : undefined}
					>
						{title}
					</h2>
					{description ? <p className="as-panel-card__description">{description}</p> : null}
				</div>
				{stamp ? <span className="as-panel-card__updated">Updated {stamp}</span> : null}
			</header>
			<div className="as-panel-card__body">
				{lead}
				{body}
			</div>
			{footer ? <div className="as-panel-card__footer">{footer}</div> : null}
		</section>
	);
}

PanelCard.propTypes = {
	/** the dataset id; used for the heading id and a data attribute */
	id: PropTypes.string,
	title: PropTypes.node.isRequired,
	description: PropTypes.node,
	/** ISO UTC stamp of the rows; omitted while loading or when nothing was computed */
	computedAt: PropTypes.string,
	status: PropTypes.oneOf(CARD_STATUSES),
	/** the thrown apiClient error, for the failed state's detail */
	error: PropTypes.shape({ status: PropTypes.number, message: PropTypes.string }),
	/** overrides the headline of the empty / missing / forbidden / unsupported states */
	message: PropTypes.string,
	/** offered in the failed state */
	onRetry: PropTypes.func,
	footer: PropTypes.node,
	/** rendered at the top of the body in every state (attached strips with their own states) */
	lead: PropTypes.node,
	children: PropTypes.node,
	className: PropTypes.string,
};

export default PanelCard;
